import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useActivePage, useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { Card, CardContent, CardHeader } from '@mui/material';

import Api from '@barboxx/api';
import { User } from '@barboxx/models';

import { RootContainer } from '../../components/nav/RootContainer';
import {
    CREATE_USER_SCHEMA,
    CreateUser,
    UserForm,
} from '../components/UserForm';

const INITIAL_VALUES: User.CreateRequest = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    avatar: '',
    userType: 'Business',
    roles: [],
    password: '',
    businesses: [],
    active: true,
};

export function UserCreate() {
    const notifications = useNotifications();
    const activePage = useActivePage();

    const [searchParams] = useSearchParams();
    const businessId = searchParams.get('businessId');

    const defaultValues = {
        ...INITIAL_VALUES,
        ...(businessId
            ? {
                  businessIds: [Number(businessId)],
              }
            : {}),
    };

    const breadcrumbs = [
        ...(activePage?.breadcrumbs ?? []),
        { title: 'Create', path: `${activePage?.path}/create` },
    ];

    const navigate = useNavigate();
    const formContext = useForm<CreateUser>({
        resolver: yupResolver(CREATE_USER_SCHEMA),
        defaultValues,
    });

    const mutateUser = useMutation({
        mutationKey: ['createUser'],
        mutationFn: (data: User.CreateRequest) => Api.Users.createUser(data),
    });

    const handleFormSave = (values: CreateUser) => {
        delete values.businessIds;

        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(
                    `Successfully created new user: "${data?.firstName}"`,
                    {
                        severity: 'success',
                    }
                );
                formContext.reset();
                navigate(`/users/${data?.id}`);
            })
            .catch(e => {
                notifications.show(e.message, { severity: 'error' });
            });
    };
    return (
        <RootContainer breadcrumbs={breadcrumbs} title="Create User">
            <Card>
                <CardHeader title="Basic Info" />
                <CardContent>
                    <UserForm
                        onSubmit={handleFormSave}
                        formContext={formContext}
                        editableOnLoad
                    />
                </CardContent>
            </Card>
        </RootContainer>
    );
}
